import type { Ref } from 'vue';
import {
  computed,
  markRaw,
  nextTick,
  onUnmounted,
  ref,
  shallowRef,
  unref,
  watch,
} from 'vue';
import dayjs from '@/utils/dayjs';
import type { Dayjs } from 'dayjs';
import { curveStepAfter, curveStepBefore, line } from 'd3';
import { orderBy, sortBy, uniqBy } from 'lodash-es';
import { toPx } from '@/utils/helpers';

// Import Rect from modular utils structure
import { Rect } from './utils/rect';

// Re-export for backward compatibility with existing imports from this file
export { Rect };

// local dev profiling helpers
const SHOULD_PROFILE = 0;
const profileKeys = ['blockComputation'];
const profile = (name: string) =>
  SHOULD_PROFILE && profileKeys.includes(name) && console.time(name);
const profileEnd = (name: string) =>
  SHOULD_PROFILE && profileKeys.includes(name) && console.timeEnd(name);

// ------------------------------ min/max date calculation ------------------------------ //

/**
 * Minimal interface for items used in min/max date calculation.
 * Allows reuse across different item types (PreparedItem, etc.)
 */
export interface IMinMaxDateItem {
  startDate?: string | null;
  dueDate?: string | null;
  minDate?: string | null;
  maxDate?: string | null;
  canHaveChildren?: boolean;
}

/**
 * Calculates the aggregate minDate and maxDate from a list of child items.
 *
 * This is the single source of truth for deriving a parent's minDate/maxDate
 * from its children. Used by both optimistic updates and cache recomputation.
 *
 * Logic:
 * - Collects all children's startDate and dueDate
 * - For folder children (canHaveChildren=true), also considers their minDate/maxDate
 *   BUT only if they extend BEYOND the child's own date range
 * - Returns the minimum of all start dates and maximum of all end dates
 *
 * @param children - Array of child items to calculate min/max from
 * @returns Object with minDate and maxDate strings (YYYY-MM-DD) or undefined
 */
export function calculateMinMaxDatesFromChildren(children: IMinMaxDateItem[]): {
  minDate: string | undefined;
  maxDate: string | undefined;
} {
  if (children.length === 0) {
    return { minDate: undefined, maxDate: undefined };
  }

  const allStartDates: ReturnType<typeof dayjs>[] = [];
  const allEndDates: ReturnType<typeof dayjs>[] = [];

  children.forEach((child) => {
    // Consider child's own dates
    if (child.startDate) {
      allStartDates.push(dayjs(child.startDate));
    }
    if (child.dueDate) {
      allEndDates.push(dayjs(child.dueDate));
    }

    // If child has children (is a folder), also consider its min/max dates
    // BUT only if they extend BEYOND the child's own dates
    if (child.canHaveChildren) {
      const childStart = child.startDate ? dayjs(child.startDate) : null;
      const childEnd = child.dueDate ? dayjs(child.dueDate) : null;

      if (child.minDate) {
        const minDate = dayjs(child.minDate);
        // Only include minDate if it's before child's own startDate
        if (!childStart || minDate.isBefore(childStart, 'day')) {
          allStartDates.push(minDate);
        }
      }
      if (child.maxDate) {
        const maxDate = dayjs(child.maxDate);
        // Only include maxDate if it's after child's own dueDate
        if (!childEnd || maxDate.isAfter(childEnd, 'day')) {
          allEndDates.push(maxDate);
        }
      }
    }
  });

  // Use date-only format (YYYY-MM-DD) to avoid timezone comparison issues
  const minDate =
    allStartDates.length > 0
      ? dayjs.min(allStartDates)?.format('YYYY-MM-DD')
      : undefined;
  const maxDate =
    allEndDates.length > 0
      ? dayjs.max(allEndDates)?.format('YYYY-MM-DD')
      : undefined;

  return { minDate, maxDate };
}

// ------------------------------ duration block types and generators ------------------------------ //

export interface IDurationBlock {
  dayOffset: number;
  left: number;
  width: number;
  isEven: boolean;
  date: Dayjs;
}

export interface IExtendedDurationBlock extends IDurationBlock {
  label: string;
  color?: string;
}

export interface ICustomBlockMarker {
  id: string;
  label: string;
  date: Dayjs | [Dayjs, Dayjs];
  color?: string;
  /**
   * if true, snapping will be enabled during drag interactions.
   * this means that items need to either fit completely within the block or
   * completely outside of it.
   **/
  enableDragInteraction?: boolean;
}

interface IDurationBlockGenParams {
  virtualScrollXOffset: number;
  containerWidth: number; // visible width
  dayBlockWidth: number;
  toleranceFactor: number;
  minDate: Dayjs;
  maxDate?: Dayjs;
}

const getYearBlocks = ({
  virtualScrollXOffset,
  containerWidth,
  dayBlockWidth,
  toleranceFactor,
  minDate,
  maxDate,
}: IDurationBlockGenParams): IDurationBlock[] => {
  const absoluteTolerance = Math.ceil(
    toleranceFactor * (containerWidth / dayBlockWidth)
  );
  const dayOffset = Math.max(
    Math.floor(virtualScrollXOffset / dayBlockWidth) - absoluteTolerance,
    0
  );
  const start = minDate.add(dayOffset, 'day').startOf('year').startOf('day');
  if (start.isAfter(maxDate)) {
    return [];
  }

  let blockCountToRender = 0;
  {
    const maxBlockCountFromCurrentOffset = maxDate
      ? maxDate.diff(start, 'year')
      : Infinity; // maxBlockCountFromCurrentOffset (including tolerance)
    const widthToExhaust =
      containerWidth + containerWidth * toleranceFactor * 2;
    let widthExhausted = 0,
      yearDate = start.clone();
    while (
      widthExhausted < widthToExhaust &&
      blockCountToRender < maxBlockCountFromCurrentOffset
    ) {
      ++blockCountToRender;
      widthExhausted += (yearDate.isLeapYear() ? 366 : 365) * dayBlockWidth;
      yearDate = yearDate.add(1, 'year');
    }
    const minItemsBasedOnTolerance = toleranceFactor * 2 + 1;
    blockCountToRender = Math.ceil(
      Math.max(blockCountToRender, minItemsBasedOnTolerance)
    );
  }

  return Array(blockCountToRender)
    .fill(0)
    .map((_, i) => {
      const yearDate = start.add(i, 'year').startOf('year').startOf('day');
      const daysSinceStart = yearDate.diff(minDate, 'day');
      const yearsSinceStart = yearDate.diff(minDate, 'year');

      return {
        dayOffset: daysSinceStart,
        left: daysSinceStart * dayBlockWidth,
        width: (yearDate.isLeapYear() ? 366 : 365) * dayBlockWidth,
        isEven: yearsSinceStart % 2 === 0,
        date: yearDate,
      };
    })
    .filter((block) => block.date.isBetween(minDate, maxDate, 'day', '[)'));
};

const getQuarterBlocks = ({
  virtualScrollXOffset,
  containerWidth,
  dayBlockWidth,
  toleranceFactor,
  minDate,
  maxDate,
}: IDurationBlockGenParams): IDurationBlock[] => {
  const yearBlocks = getYearBlocks({
    virtualScrollXOffset,
    containerWidth,
    dayBlockWidth,
    toleranceFactor,
    minDate,
    maxDate,
  });
  const quarterBlocks = yearBlocks
    .reduce<typeof yearBlocks>((acc, yearBlock) => {
      const quarters = Array(4)
        .fill(0)
        .map((_, i) => {
          const quarter = i + 1;
          const quarterDate = yearBlock.date
            .quarter(quarter)
            .startOf('quarter')
            .startOf('day');
          const daysSinceStart = quarterDate.diff(minDate, 'day');
          const quartersSinceStart = quarterDate.diff(minDate, 'quarter');
          const daysInQuarter = quarterDate
            .add(1, 'quarter')
            .diff(quarterDate, 'day');

          return {
            dayOffset: daysSinceStart,
            left: daysSinceStart * dayBlockWidth,
            width: daysInQuarter * dayBlockWidth,
            isEven: quartersSinceStart % 2 === 0,
            date: quarterDate,
          };
        });
      return [...acc, ...quarters];
    }, [])
    .filter((block) => block.date.isBetween(minDate, maxDate, 'day', '[)'));
  return quarterBlocks;
};

const getMonthBlocks = ({
  virtualScrollXOffset,
  containerWidth,
  dayBlockWidth,
  toleranceFactor,
  minDate,
  maxDate,
}: IDurationBlockGenParams): IDurationBlock[] => {
  const absoluteTolerance = Math.ceil(
    toleranceFactor * (containerWidth / dayBlockWidth)
  );
  const dayOffset = Math.max(
    Math.floor(virtualScrollXOffset / dayBlockWidth) - absoluteTolerance,
    0
  );
  const start = minDate.add(dayOffset, 'day').startOf('month').startOf('day');
  if (start.isAfter(maxDate)) {
    return [];
  }

  let blockCountToRender = 0;
  {
    const maxBlockCountFromCurrentOffset = maxDate
      ? maxDate.diff(start, 'month')
      : Infinity; // maxBlockCountFromCurrentOffset (including tolerance)
    const widthToExhaust =
      containerWidth + containerWidth * toleranceFactor * 2;
    let widthExhausted = 0,
      monthDate = start.clone();
    while (
      widthExhausted < widthToExhaust &&
      blockCountToRender < maxBlockCountFromCurrentOffset
    ) {
      ++blockCountToRender;
      widthExhausted += monthDate.daysInMonth() * dayBlockWidth;
      monthDate = monthDate.add(1, 'month');
    }
    const minItemsBasedOnTolerance = toleranceFactor * 2 + 1;
    blockCountToRender = Math.ceil(
      Math.max(blockCountToRender, minItemsBasedOnTolerance)
    );
  }

  return Array(blockCountToRender)
    .fill(0)
    .map((_, i) => {
      const monthDate = start.add(i, 'month').startOf('month').startOf('day');
      const daysSinceStart = monthDate.diff(minDate, 'day');
      const monthsSinceStart = monthDate.diff(minDate, 'month');

      return {
        dayOffset: daysSinceStart,
        left: daysSinceStart * dayBlockWidth,
        width: monthDate.daysInMonth() * dayBlockWidth,
        isEven: monthsSinceStart % 2 === 0,
        date: monthDate,
      };
    })
    .filter((block) => block.date.isBetween(minDate, maxDate, 'day', '[)'));
};

const getWeekBlocks = ({
  virtualScrollXOffset,
  containerWidth,
  dayBlockWidth,
  toleranceFactor,
  minDate,
  maxDate,
}: IDurationBlockGenParams): IDurationBlock[] => {
  const absoluteTolerance = Math.ceil(
    toleranceFactor * (containerWidth / dayBlockWidth)
  );
  const dayOffset = Math.max(
    Math.floor(virtualScrollXOffset / dayBlockWidth) - absoluteTolerance,
    0
  );
  const start = minDate.add(dayOffset, 'day').startOf('isoWeek').startOf('day');
  if (start.isAfter(maxDate)) {
    return [];
  }

  let blockCountToRender = 0;
  {
    // +1 to include whatever is left of the last week
    const maxBlockCountFromCurrentOffset = maxDate
      ? maxDate.diff(start, 'week') + 1
      : Infinity; // maxBlockCountFromCurrentOffset (including tolerance)
    const widthToExhaust =
      containerWidth + containerWidth * toleranceFactor * 2;
    let widthExhausted = 0,
      weekDate = start.clone();
    while (
      widthExhausted < widthToExhaust &&
      blockCountToRender < maxBlockCountFromCurrentOffset
    ) {
      ++blockCountToRender;
      widthExhausted += 7 * dayBlockWidth;
      weekDate = weekDate.add(1, 'week');
    }
    const minItemsBasedOnTolerance = toleranceFactor * 2 + 1;
    blockCountToRender = Math.ceil(
      Math.max(blockCountToRender, minItemsBasedOnTolerance)
    );
  }

  return Array(blockCountToRender)
    .fill(0)
    .map((_, i) => {
      const weekDate = start.add(i, 'week').startOf('isoWeek').startOf('day');
      const daysSinceStart = weekDate.diff(minDate, 'day');
      const weeksSinceStart = weekDate.diff(minDate, 'week');

      // days in week in case of partial weeks
      const daysToMax = maxDate ? maxDate.diff(weekDate, 'day') : Infinity;
      const daysInWeek = Math.min(daysToMax, 7);

      return {
        dayOffset: daysSinceStart,
        left: daysSinceStart * dayBlockWidth,
        width: daysInWeek * dayBlockWidth,
        isEven: weeksSinceStart % 2 === 0,
        date: weekDate,
      };
    })
    .filter((block) => block.date.isBetween(minDate, maxDate, 'day', '[)'));
};

const getDayBlocks = ({
  virtualScrollXOffset,
  containerWidth,
  dayBlockWidth,
  toleranceFactor,
  minDate,
  maxDate,
}: IDurationBlockGenParams): IDurationBlock[] => {
  const absoluteTolerance = Math.ceil(
    toleranceFactor * (containerWidth / dayBlockWidth)
  );
  const offset = Math.max(
    Math.floor(virtualScrollXOffset / dayBlockWidth) - absoluteTolerance,
    0
  );
  const start = minDate.add(offset, 'day').startOf('day');
  if (start.isAfter(maxDate)) {
    return [];
  }

  let blockCountToRender = 0;
  {
    const maxBlockCountFromCurrentOffset = maxDate
      ? maxDate.diff(start, 'day')
      : Infinity; // maxBlockCountFromCurrentOffset (including tolerance)
    const widthToExhaust =
      containerWidth + containerWidth * toleranceFactor * 2;
    let widthExhausted = 0,
      dayDate = start.clone();
    while (
      widthExhausted < widthToExhaust &&
      blockCountToRender < maxBlockCountFromCurrentOffset
    ) {
      ++blockCountToRender;
      widthExhausted += dayBlockWidth;
      dayDate = dayDate.add(1, 'day');
    }
    const minItemsBasedOnTolerance = toleranceFactor * 2 + 1;
    blockCountToRender = Math.ceil(
      Math.max(blockCountToRender, minItemsBasedOnTolerance)
    );
  }

  return Array(blockCountToRender)
    .fill(0)
    .map((_, i) => {
      const daysSinceStart = start.add(i, 'days').diff(minDate, 'day');

      return {
        dayOffset: offset + i,
        left: daysSinceStart * dayBlockWidth,
        width: dayBlockWidth,
        isEven: daysSinceStart % 2 === 0,
        date: start.add(i, 'days'),
      };
    })
    .filter((block) => block.date.isBetween(minDate, maxDate, 'day', '[)'));
};

const getCustomBlocks = ({
  virtualScrollXOffset,
  containerWidth,
  dayBlockWidth,
  toleranceFactor,
  minDate,
  maxDate,

  markers,
}: IDurationBlockGenParams & {
  markers: ICustomBlockMarker[];
}): IExtendedDurationBlock[] => {
  const absoluteTolerance = Math.ceil(
    toleranceFactor * (containerWidth / dayBlockWidth)
  );
  const dayOffset = Math.max(
    Math.floor(virtualScrollXOffset / dayBlockWidth) - absoluteTolerance,
    0
  );
  const start = minDate.add(dayOffset, 'day').startOf('day');
  if (start.isAfter(maxDate)) {
    return [];
  }

  const containerMinVisibleDate = start.subtract(absoluteTolerance, 'days');
  const containerMaxVisibleDate = start.add(
    absoluteTolerance * 2, // 1 to cover the visible area and 1 for the tolerance
    'days'
  );

  const markersInRange = orderBy(
    markers.filter((marker) => {
      const markerDate1 = Array.isArray(marker.date)
        ? marker.date[0]
        : marker.date;
      const isStartOverlapping = markerDate1.isBetween(
        containerMinVisibleDate,
        containerMaxVisibleDate,
        'day',
        '[]'
      );
      if (isStartOverlapping) return true;

      const markerDate2 = Array.isArray(marker.date) ? marker.date[1] : null;
      const isEndOverlapping = markerDate2?.isBetween(
        containerMinVisibleDate,
        containerMaxVisibleDate,
        'day',
        '[]'
      );
      return !!isEndOverlapping;
    }),
    (m) => (Array.isArray(m.date) ? m.date[0] : m.date).unix(),
    'asc'
  );

  const ret: IExtendedDurationBlock[] = markersInRange
    .map((marker) => {
      const markerDate = Array.isArray(marker.date)
        ? marker.date[0]
        : marker.date;
      const daysSinceStart = markerDate.diff(minDate, 'day');
      const daysInBlock = Array.isArray(marker.date)
        ? marker.date[1].diff(markerDate, 'day') + 1 // +1 to include the end date
        : 1;

      return {
        label: marker.label,
        dayOffset: daysSinceStart,
        left: daysSinceStart * dayBlockWidth,
        width: daysInBlock * dayBlockWidth,
        date: markerDate,
        isEven: false,
        color: marker.color,
      } satisfies IExtendedDurationBlock;
    })
    .filter((block) => block.date.isBetween(minDate, maxDate, 'day', '[)'));

  // padding blocks (because custom blocks can't span the entire gantt, so we need to pad them when they're not fully visible)
  const minMarkerDate = dayjs.min(
    markersInRange.map((marker) =>
      Array.isArray(marker.date) ? marker.date[0] : marker.date
    )
  ) as Dayjs | null;
  const maxMarkerDate = dayjs.max(
    markersInRange.map((marker) =>
      Array.isArray(marker.date) ? marker.date[1] : marker.date
    )
  );

  const paddingBlockStart: IExtendedDurationBlock | null = (() => {
    return {
      label: '',
      dayOffset: 0,
      left: 0,
      width:
        (minMarkerDate || maxDate || minDate).diff(minDate, 'day') *
        dayBlockWidth,
      date: minDate,
      isEven: false,
    };
  })();

  const paddingBlockEnd: IExtendedDurationBlock | null = (() => {
    if (!maxMarkerDate) return null;

    const daysSinceStart = maxMarkerDate.diff(minDate, 'day') + 1; // +1 to include the include maxMarkerDate
    const daysInBlock = maxDate?.diff(maxMarkerDate, 'day') || 0;

    return {
      label: '',
      dayOffset: daysSinceStart,
      left: daysSinceStart * dayBlockWidth,
      width: daysInBlock * dayBlockWidth,
      date: maxMarkerDate,
      isEven: false,
    };
  })();

  if (paddingBlockStart) ret.unshift(paddingBlockStart);
  if (paddingBlockEnd) ret.push(paddingBlockEnd);

  return ret;
};
// ------------------------------ generator to zoom level mapping ------------------------------ //

export enum ZOOM_LEVEL_TYPE {
  YEARS = 'years',
  QUARTERS = 'quarters',
  MONTHS = 'months',
  WEEKS = 'weeks',
  DAYS = 'days',
}

const ZOOM_LEVEL_TYPE_BLOCK_GEN_MAP = {
  [ZOOM_LEVEL_TYPE.YEARS]: getYearBlocks,
  [ZOOM_LEVEL_TYPE.QUARTERS]: getQuarterBlocks,
  [ZOOM_LEVEL_TYPE.MONTHS]: getMonthBlocks,
  [ZOOM_LEVEL_TYPE.WEEKS]: getWeekBlocks,
  [ZOOM_LEVEL_TYPE.DAYS]: getDayBlocks,
};

// ------------------------------ zoom level formatters ------------------------------ //

const formatters = {
  [ZOOM_LEVEL_TYPE.YEARS]: (date: Dayjs) => {
    return {
      block: date.format('YYYY'),
      subBlock: date.format('YY'),
    };
  },
  [ZOOM_LEVEL_TYPE.QUARTERS]: (date: Dayjs) => {
    return {
      block: date.format('YYYY/[Q]Q'),
      subBlock: date.format('[Q]Q'),
    };
  },
  [ZOOM_LEVEL_TYPE.MONTHS]: (date: Dayjs) => {
    return {
      block: date.format('MMM YYYY'),
      subBlock: date.format('MMM'),
    };
  },
  [ZOOM_LEVEL_TYPE.WEEKS]: (date: Dayjs) => {
    const start = date.startOf('isoWeek').format('D');
    const end = date.endOf('isoWeek').format('D');
    const month = date.endOf('isoWeek').format('MMM');
    const year = date.endOf('isoWeek').format('YYYY');
    return {
      block: `${start}-${end} ${month}, ${year}`,
      subBlock: `${start}-${end}`,
    };
  },
  [ZOOM_LEVEL_TYPE.DAYS]: (date: Dayjs) => {
    return {
      block: date.format('ddd, MMM D'),
      subBlock: date.format('ddd'),
    };
  },
};

// ------------------------------ zoom level presets ------------------------------ //

export interface IZoomPreset {
  type: ZOOM_LEVEL_TYPE;
  dayBlockWidth: number;
  contextualBlockType: ZOOM_LEVEL_TYPE | null;
  subBlockType: ZOOM_LEVEL_TYPE | null;
}

export const ZOOM_LEVEL_PRESETS: IZoomPreset[] = [
  // Logarithmic scale: each step doubles pixels per day
  // dayBlockWidth = px/day, so 0.25 means 1px = 4 days, 1 means 1px = 1 day, etc.

  // years (very zoomed out)
  {
    type: ZOOM_LEVEL_TYPE.YEARS,
    dayBlockWidth: 0.25, // 1px = 4 days
    contextualBlockType: null,
    subBlockType: ZOOM_LEVEL_TYPE.QUARTERS,
  },
  {
    type: ZOOM_LEVEL_TYPE.YEARS,
    dayBlockWidth: 0.5, // 1px = 2 days
    contextualBlockType: null,
    subBlockType: ZOOM_LEVEL_TYPE.QUARTERS,
  },
  {
    type: ZOOM_LEVEL_TYPE.YEARS,
    dayBlockWidth: 1, // 1px = 1 day
    contextualBlockType: null,
    subBlockType: ZOOM_LEVEL_TYPE.QUARTERS,
  },

  // quarters
  {
    type: ZOOM_LEVEL_TYPE.QUARTERS,
    dayBlockWidth: 2, // 1px = 0.5 days (12 hours)
    contextualBlockType: null,
    subBlockType: ZOOM_LEVEL_TYPE.MONTHS,
  },
  {
    type: ZOOM_LEVEL_TYPE.QUARTERS,
    dayBlockWidth: 4, // 1px = 0.25 days (6 hours)
    contextualBlockType: null,
    subBlockType: ZOOM_LEVEL_TYPE.MONTHS,
  },

  // months
  {
    type: ZOOM_LEVEL_TYPE.MONTHS,
    dayBlockWidth: 8, // 1px = 3 hours
    contextualBlockType: null,
    subBlockType: ZOOM_LEVEL_TYPE.WEEKS,
  },
  {
    type: ZOOM_LEVEL_TYPE.MONTHS,
    dayBlockWidth: 16, // 1px = 1.5 hours
    contextualBlockType: null,
    subBlockType: ZOOM_LEVEL_TYPE.WEEKS,
  },

  // weeks
  {
    type: ZOOM_LEVEL_TYPE.WEEKS,
    dayBlockWidth: 32, // 1px = 45 minutes
    contextualBlockType: null,
    subBlockType: ZOOM_LEVEL_TYPE.DAYS,
  },
  {
    type: ZOOM_LEVEL_TYPE.WEEKS,
    dayBlockWidth: 64, // 1px = ~22 minutes
    contextualBlockType: null,
    subBlockType: ZOOM_LEVEL_TYPE.DAYS,
  },

  // days (very zoomed in)
  {
    type: ZOOM_LEVEL_TYPE.DAYS,
    dayBlockWidth: 100, // 1px = ~14 minutes
    contextualBlockType: ZOOM_LEVEL_TYPE.YEARS,
    subBlockType: null,
  },
];

// ------------------------------ gantt item and relations ------------------------------ //

export type ConnectionType = ['start' | 'end', 'start' | 'end'];

export interface IGanttRelationDef {
  id: string;
  label: string;
  /**
   * IA = Item Current = Item from which the line originates
   * IB = Item Other = Item to which the line goes
   *
   * `dependency` - IB is a dependency of IA (IB -> IA)
   * `relation` - IA and IB are related, but neither is dependent on the other. Non-directional. (IA --- IB)
   *
   * @example
   * `const item = { id: 'i1', ..., relations: [{ itemId: 'i2', relationId: 'r1' }] }`
   * Here `i1` will correspond to IA and `i2` will correspond to IB.
   * If the relation is `dependency`, then `i2` is a dependency of `i1`.
   * If the relation is `association`, then `i1` and `i2` are associated in a non-directional way.
   */
  type: 'dependency' | 'association';

  /** [other, current] */
  connectionType: ConnectionType;
  color: string;
  strokeWidth?: number;
  strokeDasharray?: string;
}

export interface IGanttItemShadow {
  id: string;
  label?: string;
  startDate: Dayjs;
  endDate: Dayjs;
  color: string;
}

export interface IGanttItem {
  id: string;
  type: 'wrapper' | 'group' | 'task' | 'milestone' | 'stack';
  order: number | string;

  title: string;

  startDate?: Dayjs;
  endDate?: Dayjs;

  minTreeDate?: Dayjs;
  maxTreeDate?: Dayjs;

  /**
   * to be used in conjunction with `lazy` mode. Expect weird behavior otherwise.
   */
  childrenLoaded?: boolean;

  color: string;

  parentId: string | null;

  relations: Array<{
    itemId: IGanttItem['id'];
    relationDefId: IGanttRelationDef['id'];
  }>;

  shadows?: IGanttItemShadow[];

  visualAttributes?: {
    /** progress percent for the item; only works for `group` and `task` types. 0-100. */
    progress?: number;
    isDone?: boolean; // TODO: implement other statuses by getting rid of this
    highlighted?: boolean;
    /** highlights the item in red with the string as tooltip */
    error?: string;
  };
}

export interface IExtendedGanttItem extends IGanttItem {
  /**
   * startDate is optional but we still need a date to make everything function properly.
   * The value for this should *always* be set to the startDate if it exists and a fallback
   * date otherwise. Internally, only this should be used in calculations and places where a
   * date is needed to function (GanttBar resizing/moving).
   */
  _internalStartDate: Dayjs;
  /**
   * endDate is optional but we still need a date to make everything function properly.
   * The value for this should *always* be set to the endDate if it exists and a fallback
   * date otherwise. Internally, only this should be used in calculations and places where a
   * date is needed to function (GanttBar resizing/moving).
   */
  _internalEndDate: Dayjs;

  _internalOrder: number;
  minTreeDate: Dayjs;
  maxTreeDate: Dayjs;
  treeHeight: number;

  /** to be used for deciding whether to render the item or not */
  _minRenderDate: Dayjs;
  /** to be used for deciding whether to render the item or not */
  _maxRenderDate: Dayjs;

  /**
   * The level of the item in the hierarchy. Starts at 0.
   */
  hierarchyLevel: number;

  parent: IExtendedGanttItem | null;
  children: IExtendedGanttItem[];

  dependencies: Array<{
    item: IExtendedGanttItem;
    relationDef: IGanttRelationDef;
  }>; // items this item depends on
  dependents: Array<{
    item: IExtendedGanttItem;
    relationDef: IGanttRelationDef;
  }>; // items that depend on this item
  associations: Array<{
    item: IExtendedGanttItem;
    relationDef: IGanttRelationDef;
  }>; // items that are associated with this item
}

export interface IExtendedGanttItemWithCollapse extends IExtendedGanttItem {
  isCollapsed: boolean;
  collapsedTreeHeight: number;
}

export interface IExtendedGanttItemWithRect
  extends IExtendedGanttItemWithCollapse {
  rect: {
    left: number;
    top: number;
    width: number;
    height: number;

    containerLeft: number;
    containerTop: number;
    containerWidth: number;
    containerHeight: number;

    shadows: {
      [shadowId: IGanttItemShadow['id']]: {
        left: number;
        top: number;
        width: number;
        height: number;
      };
    };
  };
}

export interface IGanttItemRelation {
  id: string;
  itemCurrent: IExtendedGanttItem; // item on which the relation is defined
  itemOther: IExtendedGanttItem; // item which is related to the current item
  relationDef: IGanttRelationDef;
  minDate: Dayjs;
  maxDate: Dayjs;
  minOrder: number;
  maxOrder: number;
}

export interface IGanttItemRelationWithRect extends IGanttItemRelation {
  itemCurrent: IExtendedGanttItemWithRect;
  itemOther: IExtendedGanttItemWithRect;
}

// ------------------------------ useGantt and related utils ------------------------------ //

export function getDepId(
  itemCurrent: { id: string },
  itemOther: { id: string },
  relationDef: Pick<IGanttRelationDef, 'id' | 'type'>
) {
  if (relationDef.type === 'dependency') {
    return `${itemOther.id}---[${relationDef.id}]--->${itemCurrent.id}`;
  } else {
    // make sure the id is always the same for the same relation so we can remove duplicates
    const [aId, bId] = [itemCurrent.id, itemOther.id].sort();
    return `${aId}---[${relationDef.id}]---${bId}`;
  }
}

const TOLERANCE_FACTOR_X = 1; // 1 full screen to the left and right (3 screens total)
const TOLERANCE_FACTOR_Y = 0.5; // 1/2 screen to the top and bottom (2 screens total)

export const useGantt = ({
  initialZoomLevel,
  virtualScrollXOffset,
  visibleContainerWidth,
  minDate,
  maxDate,

  headerHeightsConfig,

  lazy,
  ganttItems,
  relationDefs,
  virtualScrollYOffset,
  visibleContainerHeight,
  barHeight,
  barsContainerYPadding,

  customBlockMarkers,
  initialExpandedItemIds,
  initialCollapsedItemIds,
}: {
  initialZoomLevel?: ZOOM_LEVEL_TYPE;

  virtualScrollXOffset: Ref<number>;
  visibleContainerWidth: Ref<number>;
  minDate: Ref<Dayjs>;
  maxDate: Ref<Dayjs>;

  headerHeightsConfig: Ref<IHeaderHeightsConfig>;

  /**
   * when true, minTreeDate and maxTreeDate are not calculated by the component, instead
   * they need to be provided through the ganttItems prop. Component will throw if this is not the case.
   */
  lazy: Ref<boolean>;
  ganttItems: Ref<IGanttItem[]>;
  relationDefs: Ref<IGanttRelationDef[]>;
  virtualScrollYOffset: Ref<number>;
  visibleContainerHeight: Ref<number>;
  barHeight: Ref<number>;
  /**
   * vertical padding between the container and the first/last bars
   * measured in number of bars (which is internally converted to pixels by multiplying by barHeight)
   */
  barsContainerYPadding: Ref<number>;

  customBlockMarkers: Ref<ICustomBlockMarker[] | undefined>;

  /**
   * List of item IDs that should start expanded (not collapsed) when in lazy mode.
   * Items in this list will not be auto-collapsed by the lazy mode initialization.
   */
  initialExpandedItemIds?: Ref<IGanttItem['id'][]>;

  /**
   * List of item IDs that should start collapsed when in lazy mode.
   * Used to restore collapsed state for items that are not auto-collapsed by lazy mode
   * (e.g., initiatives which always have childrenLoaded: true).
   */
  initialCollapsedItemIds?: Ref<IGanttItem['id'][]>;
}) => {
  const currentZoomLevelIdx = shallowRef<number>(0); // default to lowest

  if (initialZoomLevel) {
    const initialZoomLevelIdx = ZOOM_LEVEL_PRESETS.findIndex(
      (preset) => preset.type === initialZoomLevel
    );
    if (initialZoomLevelIdx !== -1) {
      currentZoomLevelIdx.value = initialZoomLevelIdx;
    }
  }

  // ----------------- getters ----------------- //

  const currentZoomLevelPreset = computed(
    () => ZOOM_LEVEL_PRESETS[currentZoomLevelIdx.value]
  );

  const zoomLevelConfig = computed(() => {
    return currentZoomLevelPreset.value;
  });

  const previousZoomLevelConfig = computed(() => {
    if (currentZoomLevelIdx.value === 0) return null;
    return ZOOM_LEVEL_PRESETS[currentZoomLevelIdx.value - 1];
  });
  const nextZoomLevelConfig = computed(() => {
    if (currentZoomLevelIdx.value === ZOOM_LEVEL_PRESETS.length - 1)
      return null;
    return ZOOM_LEVEL_PRESETS[currentZoomLevelIdx.value + 1];
  });
  const stackedHeadersCount = computed(() => {
    let count = 1; // 1 for the main header
    if (currentZoomLevelPreset.value.subBlockType) {
      count++;
    }
    if (currentZoomLevelPreset.value.contextualBlockType) {
      count++;
    }
    if (customBlockMarkers.value?.length) {
      count++;
    }
    return count;
  });

  // keeps track of the current day
  const today = shallowRef<Dayjs>(dayjs().startOf('day'));
  let todayTimerId: number | null = null;
  const updateToday = () => {
    const msTillTomorrow = dayjs().endOf('day').diff(dayjs());
    todayTimerId = window.setTimeout(() => {
      today.value = dayjs().startOf('day');
      updateToday();
    }, msTillTomorrow);
  };
  updateToday();
  onUnmounted(() => {
    if (todayTimerId) clearTimeout(todayTimerId);
  });

  // ----------------- blocks ----------------- //

  const blocksToRender = shallowRef<IExtendedDurationBlock[]>([]);
  const subBlocksToRender = shallowRef<IExtendedDurationBlock[]>([]);
  const contextualBlocksToRender = shallowRef<IExtendedDurationBlock[]>([]);
  const customBlocksToRender = shallowRef<IExtendedDurationBlock[]>([]);

  watch(
    [
      virtualScrollXOffset,
      currentZoomLevelPreset,
      visibleContainerWidth,
      customBlockMarkers,
    ],
    () => {
      profile('blockComputation');

      // ----------------- blocks ----------------- //

      const blockGenFn =
        ZOOM_LEVEL_TYPE_BLOCK_GEN_MAP[currentZoomLevelPreset.value.type];
      const blockType = currentZoomLevelPreset.value.type;
      const blocks = blockGenFn({
        virtualScrollXOffset: virtualScrollXOffset.value,
        containerWidth: visibleContainerWidth.value,
        toleranceFactor: TOLERANCE_FACTOR_X,
        dayBlockWidth: zoomLevelConfig.value.dayBlockWidth,
        minDate: minDate.value,
        maxDate: maxDate.value,
      }).map((i) => ({
        ...i,
        label: formatters[blockType](i.date).block,
      }));
      blocksToRender.value = blocks;

      // ----------------- sub blocks ----------------- //

      const subBlockType = currentZoomLevelPreset.value.subBlockType;
      const subBlockGenFn = subBlockType
        ? ZOOM_LEVEL_TYPE_BLOCK_GEN_MAP[subBlockType]
        : null;

      const subBlocks =
        subBlockType && subBlockGenFn
          ? subBlockGenFn({
              virtualScrollXOffset: virtualScrollXOffset.value,
              containerWidth: visibleContainerWidth.value,
              toleranceFactor: TOLERANCE_FACTOR_X,
              dayBlockWidth: zoomLevelConfig.value.dayBlockWidth,
              minDate: minDate.value,
              maxDate: maxDate.value,
            }).map((i) => ({
              ...i,
              label: formatters[subBlockType](i.date).subBlock,
            }))
          : [];
      subBlocksToRender.value = subBlocks;

      // ----------------- contextual blocks ----------------- //

      const contextualBlockType =
        currentZoomLevelPreset.value.contextualBlockType;
      const contextualBlockGenFn = contextualBlockType
        ? ZOOM_LEVEL_TYPE_BLOCK_GEN_MAP[contextualBlockType]
        : null;

      const contextualBlocks =
        contextualBlockType && contextualBlockGenFn
          ? contextualBlockGenFn({
              virtualScrollXOffset: virtualScrollXOffset.value,
              containerWidth: visibleContainerWidth.value,
              toleranceFactor: TOLERANCE_FACTOR_X,
              dayBlockWidth: zoomLevelConfig.value.dayBlockWidth,
              minDate: minDate.value,
              maxDate: maxDate.value,
            }).map((i) => {
              return {
                ...i,
                label: formatters[contextualBlockType](i.date).block,
              };
            })
          : [];
      contextualBlocksToRender.value = contextualBlocks;

      // ----------------- custom blocks ----------------- //

      const customBlocks = customBlockMarkers.value?.length
        ? getCustomBlocks({
            virtualScrollXOffset: virtualScrollXOffset.value,
            containerWidth: visibleContainerWidth.value,
            toleranceFactor: TOLERANCE_FACTOR_X,
            dayBlockWidth: zoomLevelConfig.value.dayBlockWidth,
            minDate: minDate.value,
            maxDate: maxDate.value,
            markers: customBlockMarkers.value || [],
          })
        : [];
      customBlocksToRender.value = customBlocks;

      profileEnd('blockComputation');
    },
    { immediate: true }
  );

  // ----------------- programmatic scroll ----------------- //

  const onProgrammaticScrollOffsetChangeReactor = shallowRef(0);

  // Track active scroll animation so it can be cancelled if a new scroll starts
  let scrollAnimationId: number | null = null;

  /**
   * Ease-in-out cubic timing function for smooth scroll animations.
   * Returns a value between 0 and 1 based on progress (0-1).
   */
  function easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  interface ScrollToDateOptions {
    /** Whether to animate the scroll. Default: false */
    animate?: boolean;
    /** Animation duration in milliseconds. Default: 1200 */
    duration?: number;
  }

  function scrollToDate(date: Dayjs, options?: ScrollToDateOptions) {
    const { animate = false, duration = 1200 } = options ?? {};

    // Cancel any ongoing animation
    if (scrollAnimationId !== null) {
      cancelAnimationFrame(scrollAnimationId);
      scrollAnimationId = null;
    }

    const dateOffset = date.diff(minDate.value, 'day');
    const targetLeft = Math.max(
      dateOffset * zoomLevelConfig.value.dayBlockWidth -
        visibleContainerWidth.value / 2 +
        zoomLevelConfig.value.dayBlockWidth / 2,
      0
    );

    if (!animate) {
      // Instant scroll (original behavior)
      virtualScrollXOffset.value = targetLeft;
      ++onProgrammaticScrollOffsetChangeReactor.value;
      return;
    }

    // Animated scroll with ease-in-out timing
    const startLeft = virtualScrollXOffset.value;
    const distance = targetLeft - startLeft;
    const startTime = performance.now();

    function animateScroll(currentTime: number) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeInOutCubic(progress);

      virtualScrollXOffset.value = startLeft + distance * easedProgress;
      ++onProgrammaticScrollOffsetChangeReactor.value;

      if (progress < 1) {
        scrollAnimationId = requestAnimationFrame(animateScroll);
      } else {
        scrollAnimationId = null;
      }
    }

    scrollAnimationId = requestAnimationFrame(animateScroll);
  }

  function scrollToToday() {
    scrollToDate(dayjs());
  }

  function scrollToRowIndex(rowIndex: number) {
    const computedTop =
      rowIndex * barHeight.value - visibleContainerHeight.value / 2;
    virtualScrollYOffset.value = Math.max(computedTop, 0);
    ++onProgrammaticScrollOffsetChangeReactor.value;
  }

  /**
   * Scrolls to a specific center point (both horizontal date and vertical row index).
   * Either parameter can be null to skip that axis.
   */
  function scrollToCenterPoint(
    horizontalDate: Dayjs | null,
    verticalRowIndex: number | null
  ) {
    if (horizontalDate) {
      const dateOffset = horizontalDate.diff(minDate.value, 'day');
      const computedLeft =
        dateOffset * zoomLevelConfig.value.dayBlockWidth -
        visibleContainerWidth.value / 2 +
        zoomLevelConfig.value.dayBlockWidth / 2;
      virtualScrollXOffset.value = Math.max(computedLeft, 0);
    }
    if (verticalRowIndex !== null) {
      const computedTop =
        verticalRowIndex * barHeight.value - visibleContainerHeight.value / 2;
      virtualScrollYOffset.value = Math.max(computedTop, 0);
    }
    ++onProgrammaticScrollOffsetChangeReactor.value;
  }

  // Cleanup scroll animation on unmount
  onUnmounted(() => {
    if (scrollAnimationId !== null) {
      cancelAnimationFrame(scrollAnimationId);
      scrollAnimationId = null;
    }
  });

  // ----------------- positioning ----------------- //

  // we're calculating this based on virtualScrollXOffset so it's not dependent on blocksToRender
  // this way useGanttItems should get the min/max dates first, while useGanttZoomPresets can get the blocks
  const minRenderedDate = computed(() => {
    let firstBlockOffset = Math.floor(
      virtualScrollXOffset.value / zoomLevelConfig.value.dayBlockWidth
    );
    const absoluteTolerance = Math.ceil(
      TOLERANCE_FACTOR_X *
        (visibleContainerWidth.value / zoomLevelConfig.value.dayBlockWidth)
    );
    firstBlockOffset -= absoluteTolerance;
    const firstBlockDate = minDate.value
      .add(firstBlockOffset, 'day')
      .startOf('day');
    return firstBlockDate;
  });
  const maxRenderedDate = computed(() => {
    let lastBlockOffset = Math.ceil(
      (virtualScrollXOffset.value + visibleContainerWidth.value) /
        zoomLevelConfig.value.dayBlockWidth
    );
    const absoluteTolerance = Math.ceil(
      TOLERANCE_FACTOR_X *
        (visibleContainerWidth.value / zoomLevelConfig.value.dayBlockWidth)
    );
    lastBlockOffset += absoluteTolerance;
    const lastBlockDate = minDate.value
      .add(lastBlockOffset, 'day')
      .startOf('day');
    return lastBlockDate;
  });

  const combinedHeaderHeight = computed(() => {
    if (stackedHeadersCount.value === 0) {
      return 0;
    }
    return (
      headerHeightsConfig.value.main + // main header
      (stackedHeadersCount.value - 1) * headerHeightsConfig.value.sub // sub headers (0 or more)
    );
  });

  function getDateRect(date: Dayjs): Rect {
    const dateOffset = date.startOf('day').diff(minDate.value, 'day');
    return new Rect({
      x: dateOffset * zoomLevelConfig.value.dayBlockWidth,
      y: 0,
      width: zoomLevelConfig.value.dayBlockWidth,
      height: 0,
    });
  }

  function getDateByLeftOffset(leftOffset: number): Dayjs {
    const dateOffset = Math.floor(
      leftOffset / zoomLevelConfig.value.dayBlockWidth
    );
    const date = minDate.value.add(dateOffset, 'day');
    if (date.isBefore(minDate.value)) {
      // inclusive
      return minDate.value;
    }
    if (date.isSameOrAfter(maxDate.value)) {
      // exclusive
      return maxDate.value;
    }
    return date;
  }

  // ----------------- zooming / view switching ----------------- //

  const canZoomIn = computed(
    () => currentZoomLevelIdx.value < ZOOM_LEVEL_PRESETS.length - 1
  );
  const canZoomOut = computed(() => currentZoomLevelIdx.value > 0);

  const approxCurrentlyCenteredDate = computed(() => {
    const centerOffset =
      virtualScrollXOffset.value +
      visibleContainerWidth.value / 2 -
      zoomLevelConfig.value.dayBlockWidth / 2;
    const daysSinceStart = Math.ceil(
      centerOffset / zoomLevelConfig.value.dayBlockWidth
    );
    return minDate.value.add(daysSinceStart, 'days');
  });

  // Approximate row index at the vertical center of the visible area
  const approxCurrentlyCenteredRowIndex = computed(() => {
    const centerOffset =
      virtualScrollYOffset.value + visibleContainerHeight.value / 2;
    const rowIndex = Math.floor(centerOffset / barHeight.value);
    return Math.max(0, rowIndex);
  });

  async function incrementZoomLevel() {
    if (currentZoomLevelIdx.value < ZOOM_LEVEL_PRESETS.length - 1) {
      const currentlyCenteredDate = approxCurrentlyCenteredDate.value;
      currentZoomLevelIdx.value++;
      await nextTick();
      scrollToDate(currentlyCenteredDate);
    }
  }

  async function decrementZoomLevel() {
    if (currentZoomLevelIdx.value > 0) {
      const currentlyCenteredDate = approxCurrentlyCenteredDate.value;
      currentZoomLevelIdx.value--;
      await nextTick();
      scrollToDate(currentlyCenteredDate);
    }
  }

  async function setZoomLevel(zoomLevel: ZOOM_LEVEL_TYPE) {
    const currentlyCenteredDate = approxCurrentlyCenteredDate.value;
    const idx = ZOOM_LEVEL_PRESETS.findIndex(
      (level) => level.type === zoomLevel
    );
    if (idx === -1) {
      throw new Error(
        `Invalid zoom level: ${zoomLevel}. This is a bug in the component.`
      );
    }
    currentZoomLevelIdx.value = idx;
    await nextTick();
    scrollToDate(currentlyCenteredDate);
  }

  // Track zoom operation to prevent race conditions when slider fires rapidly
  let zoomTargetDate: Dayjs | null = null;
  let zoomScrollTimer: number | null = null;
  let zoomResetTimer: ReturnType<typeof setTimeout> | null = null;

  // Debounce time for zoom slider - 500ms balances race condition safety with responsiveness
  const ZOOM_INTERACTION_DEBOUNCE_MS = 500;

  /** Calculates centered date from explicit params (avoids reactive zoom level dependency) */
  function calculateCenteredDate(
    scrollXOffset: number,
    containerWidth: number,
    dayBlockWidth: number
  ): Dayjs {
    const centerOffset = scrollXOffset + containerWidth / 2 - dayBlockWidth / 2;
    const daysSinceStart = Math.ceil(centerOffset / dayBlockWidth);
    return minDate.value.add(daysSinceStart, 'days');
  }

  /** Sets zoom level index. If skipScroll is true, preserves current center without scrolling. */
  function setZoomLevelIdx(idx: number, options?: { skipScroll?: boolean }) {
    if (idx < 0 || idx >= ZOOM_LEVEL_PRESETS.length) return;

    // If skipScroll is true, just update zoom and clear any pending scroll
    if (options?.skipScroll) {
      currentZoomLevelIdx.value = idx;
      if (zoomScrollTimer) {
        cancelAnimationFrame(zoomScrollTimer);
        zoomScrollTimer = null;
      }
      if (zoomResetTimer) {
        clearTimeout(zoomResetTimer);
        zoomResetTimer = null;
      }
      zoomTargetDate = null;
      return;
    }

    // Capture center date BEFORE zoom change (once per drag sequence to prevent race conditions)
    if (zoomTargetDate === null) {
      zoomTargetDate = calculateCenteredDate(
        virtualScrollXOffset.value,
        visibleContainerWidth.value,
        zoomLevelConfig.value.dayBlockWidth
      );
    }

    // Update zoom immediately for responsive UI
    currentZoomLevelIdx.value = idx;

    // Use requestAnimationFrame for frame-aligned scroll adjustment
    if (zoomScrollTimer) {
      cancelAnimationFrame(zoomScrollTimer);
    }
    zoomScrollTimer = requestAnimationFrame(() => {
      if (zoomTargetDate) {
        scrollToDate(zoomTargetDate);
        // DON'T reset zoomTargetDate here - keep it for the entire drag sequence
      }
      zoomScrollTimer = null;

      // Only start the reset timer AFTER scroll animation completes to prevent race condition
      // where zoomTargetDate could be cleared while a scroll is still pending
      if (zoomResetTimer) {
        clearTimeout(zoomResetTimer);
      }
      zoomResetTimer = setTimeout(() => {
        zoomTargetDate = null;
        zoomResetTimer = null;
      }, ZOOM_INTERACTION_DEBOUNCE_MS);
    });
  }

  /** Cleanup zoom timers on unmount to prevent memory leaks */
  function cleanupZoomTimers() {
    if (zoomScrollTimer) {
      cancelAnimationFrame(zoomScrollTimer);
      zoomScrollTimer = null;
    }
    if (zoomResetTimer) {
      clearTimeout(zoomResetTimer);
      zoomResetTimer = null;
    }
    zoomTargetDate = null;
  }

  onUnmounted(cleanupZoomTimers);

  /** Finds the zoom level index with the closest dayBlockWidth to the target value */
  function findClosestZoomLevelIdx(targetDayBlockWidth: number): number {
    let closestIdx = 0;
    let closestDiff = Math.abs(
      ZOOM_LEVEL_PRESETS[0].dayBlockWidth - targetDayBlockWidth
    );

    for (let i = 1; i < ZOOM_LEVEL_PRESETS.length; i++) {
      const diff = Math.abs(
        ZOOM_LEVEL_PRESETS[i].dayBlockWidth - targetDayBlockWidth
      );
      if (diff < closestDiff) {
        closestDiff = diff;
        closestIdx = i;
      }
    }

    return closestIdx;
  }

  const zoomLevelCount = ZOOM_LEVEL_PRESETS.length;

  // *********************************************** ITEMS *********************************************** //

  // ----------------- layer 1: raw data to consumable structure ----------------- //
  // layer 1 data should be used sparingly by elements other than layer 2

  const denormalizedGanttItems = computed(() => {
    profile('denormalizeGanttItems');

    const itemsWithNoMinDate = new Set<IGanttItem['id']>();
    const itemsWithNoMaxDate = new Set<IGanttItem['id']>();

    const relationDefMap = new Map<IGanttRelationDef['id'], IGanttRelationDef>(
      relationDefs.value.map((def) => [def.id, def])
    );

    const defaultDate = today.value;
    const isLazyMode = lazy.value;

    // Combined pass: create extended items AND build map simultaneously
    // This eliminates a separate O(n) pass for map construction
    const itemsMap = new Map<string, IExtendedGanttItem>();
    const baseExtendedItems: IExtendedGanttItem[] = [];

    for (const item of ganttItems.value) {
      let startDate = item.startDate;
      let endDate = item.endDate;
      if (!startDate) {
        startDate = endDate || defaultDate; // prefer the set date over default
      }
      if (!endDate) {
        endDate = startDate;
      }
      const isLazy =
        isLazyMode && (item.type === 'wrapper' || item.type === 'group');

      if (!item.minTreeDate) {
        itemsWithNoMinDate.add(item.id);
      }
      if (!item.maxTreeDate) {
        itemsWithNoMaxDate.add(item.id);
      }

      const initialMinTreeDate = isLazy
        ? item.minTreeDate || startDate
        : startDate;
      const initialMaxTreeDate = isLazy ? item.maxTreeDate || endDate : endDate;

      // match structure with IExtendedGanttItem
      const baseExtendedItem: IExtendedGanttItem = Object.assign({}, item, {
        _internalOrder: 0,
        _internalStartDate: startDate,
        _internalEndDate: endDate,

        _minRenderDate: initialMinTreeDate,
        _maxRenderDate: initialMaxTreeDate,

        minTreeDate: initialMinTreeDate,
        maxTreeDate: initialMaxTreeDate,

        treeHeight: 0,
        hierarchyLevel: 0,
        parent: null,
        children: [],
        dependencies: [],
        dependents: [],
        associations: [],
      });

      baseExtendedItems.push(baseExtendedItem);
      itemsMap.set(item.id, baseExtendedItem);
    }

    // Populate: children, dependencies, dependents, associations
    // Pending: _internalStartDate, _internalEndDate, _minRenderDate, _maxRenderDate, minTreeDate, maxTreeDate, treeHeight, hierarchyLevel, _internalOrder
    let extendedItems: IExtendedGanttItem[] = [];
    for (const item of baseExtendedItems) {
      const parent = itemsMap.get(item.parentId || '');
      if (parent) {
        parent.children.push(item);
        item.parent = parent;
      } else {
        extendedItems.push(item);
      }

      const dependencies: IExtendedGanttItem['dependencies'] =
        item.dependencies;
      const associations: IExtendedGanttItem['associations'] =
        item.associations;

      item.relations.forEach((relation) => {
        const def = relationDefMap.get(relation.relationDefId);
        const otherItem = itemsMap.get(relation.itemId);
        if (!def || !otherItem) return null;

        // Use markRaw to prevent Vue from creating reactive proxies for cross-references
        // This significantly reduces memory overhead with large item counts
        if (def.type === 'dependency') {
          dependencies.push(markRaw({ item: otherItem, relationDef: def }));
          otherItem.dependents.push(markRaw({ item, relationDef: def }));
        } else if (def.type === 'association') {
          associations.push(markRaw({ item: otherItem, relationDef: def }));
          otherItem.associations.push(markRaw({ item, relationDef: def }));
        }
      });

      item.dependencies = uniqBy(dependencies, (di) => di.item.id);
      item.associations = uniqBy(associations, (ai) => ai.item.id);
    }

    /** call when one or both dates of an item are missing */
    const inferMissingDates = (
      item: IExtendedGanttItem,
      parent: IExtendedGanttItem
    ) => {
      if (item.type === 'wrapper' || item.type === 'group') {
        // container items

        if (!item.startDate && !item.endDate) {
          // no dates set, try to use min/max dates as much as possible and fallback to parent's dates
          if (
            !itemsWithNoMinDate.has(item.id) &&
            !itemsWithNoMaxDate.has(item.id)
          ) {
            item._internalStartDate = item.minTreeDate;
            item._internalEndDate = item.maxTreeDate;
          } else if (!itemsWithNoMinDate.has(item.id)) {
            item._internalStartDate = item.minTreeDate;
            item._internalEndDate = item.minTreeDate;
          } else if (!itemsWithNoMaxDate.has(item.id)) {
            item._internalStartDate = item.maxTreeDate;
            item._internalEndDate = item.maxTreeDate;
          } else {
            // use parent's dates
            item._internalStartDate = parent._internalStartDate;
            item._internalEndDate = parent._internalStartDate;
          }
        } else if (item.startDate) {
          // only startDate is set, try to use maxTreeDate as much as possible and fallback to startDate
          if (!itemsWithNoMaxDate.has(item.id)) {
            item._internalEndDate = item.maxTreeDate;
          } else {
            item._internalEndDate = item._internalStartDate;
          }
        } else if (item.endDate) {
          // only endDate is set, try to use minTreeDate as much as possible and fallback to endDate
          if (!itemsWithNoMinDate.has(item.id)) {
            item._internalStartDate = item.minTreeDate;
          } else {
            item._internalStartDate = item._internalEndDate;
          }
        }

        // set min/max tree dates if not set
        if (itemsWithNoMinDate.has(item.id)) {
          item.minTreeDate = item._internalStartDate;
        }
        if (itemsWithNoMaxDate.has(item.id)) {
          item.maxTreeDate = item._internalEndDate;
        }

        //  swap if values are wrong
        if (item.maxTreeDate.isBefore(item.minTreeDate)) {
          [item.minTreeDate, item.maxTreeDate] = [
            item.maxTreeDate,
            item.minTreeDate,
          ];
        }

        // fix against inferred start/end dates
        if (item.minTreeDate.isAfter(item._internalStartDate)) {
          item.minTreeDate = item._internalStartDate;
        }
        if (item.maxTreeDate.isBefore(item._internalEndDate)) {
          item.maxTreeDate = item._internalEndDate;
        }
      } else {
        // non-container items

        if (!item.startDate && !item.endDate) {
          item._internalStartDate = parent._internalStartDate;
          item._internalEndDate = parent._internalStartDate;
        } else if (item.startDate) {
          item._internalEndDate = item.startDate;
        } else if (item.endDate) {
          item._internalStartDate = item.endDate;
        }
      }
    };

    // Populate: _internalStartDate, _internalEndDate, minTreeDate, maxTreeDate, treeHeight, hierarchyLevel
    // also sort everything by hierarchy and startDate (not the case right now)
    // also sort everything by external order
    // Pending: _internalOrder, _minRenderDate, _maxRenderDate
    const traverse = (item: IExtendedGanttItem, level: number) => {
      item.hierarchyLevel = level;
      item.treeHeight++;
      item.children = sortBy(item.children, (i) => i.order);
      item.children.forEach((child) => {
        if (!child.startDate || !child.endDate) inferMissingDates(child, item);

        traverse(child, level + 1);
        item.treeHeight += child.treeHeight;

        if (!lazy.value) {
          item.minTreeDate = dayjs.min(item.minTreeDate, child.minTreeDate);
          item.maxTreeDate = dayjs.max(item.maxTreeDate, child.maxTreeDate);
        } else {
          // lazy mode: provided minTreeDate and maxTreeDate are assumed to be correct.
          //
          // There's SOME handling above (inferMissingDates) to set the min/max dates when
          // they're not provided even in lazy mode, but the detection is not exhaustive and
          // it's not guaranteed to work in all cases. So ideally they should be provided in all cases in lazy mode.
        }
      });
    };
    extendedItems = sortBy(extendedItems, (i) => i.order);
    extendedItems.forEach((item) => {
      traverse(item, 0);
    });

    // almost done, flatten everything while preserving and populating _internalOrder and sync collapsedTreeHeight
    // also populate _minRenderDate and _maxRenderDate
    let internalOrder = 0; // 0-based
    const ret: IExtendedGanttItem[] = [];
    const flatten = (item: IExtendedGanttItem) => {
      item._internalOrder = internalOrder++;

      // Collect shadow dates in single pass instead of two .map() calls
      const shadowDates =
        item.shadows?.flatMap((s) => [s.startDate, s.endDate]) || [];
      const dates = [
        item._internalStartDate,
        item._internalEndDate,
        item.minTreeDate,
        item.maxTreeDate,
        ...shadowDates,
      ];
      item._minRenderDate = dayjs.min(...dates) || dayjs();
      item._maxRenderDate = dayjs.max(...dates) || dayjs();
      ret.push(item);
      item.children.forEach(flatten);
    };
    extendedItems.forEach(flatten);

    profileEnd('denormalizeGanttItems');

    return ret;
  });

  const ganttRelationships = computed(() => {
    profile('ganttRelationships');
    const existingRelations = new Set<IGanttItemRelation['id']>();
    const ret: IGanttItemRelation[] = [];
    denormalizedGanttItems.value.forEach((itemCurrent) => {
      const relations = [
        ...itemCurrent.dependencies,
        ...itemCurrent.associations,
      ];
      relations.forEach((relationship) => {
        const itemOther = relationship.item;
        const relationDef = relationship.relationDef;
        const relationId = getDepId(itemCurrent, itemOther, relationDef);
        if (existingRelations.has(relationId)) return; // already added from the other side
        existingRelations.add(relationId);

        const minDate = dayjs.min(
          itemCurrent._internalStartDate,
          itemOther._internalStartDate
        );
        const maxDate = dayjs.max(
          itemCurrent._internalEndDate,
          itemOther._internalEndDate
        );
        const minOrder = Math.min(
          itemCurrent._internalOrder,
          itemOther._internalOrder
        );
        const maxOrder = Math.max(
          itemCurrent._internalOrder,
          itemOther._internalOrder
        );
        ret.push({
          id: relationId,
          itemOther,
          itemCurrent,
          relationDef,
          minDate,
          maxDate,
          minOrder,
          maxOrder,
        });
      });
    });
    profileEnd('ganttRelationships');
    return ret;
  });

  // ----------------- layer 2: internally consumable data after accounting for collapsed items ----------------- //
  // layer 2 data should be used in most cases in layers 3

  const collapsedItemTreesMap = ref(new Map<IGanttItem['id'], boolean>());

  // Reactive counter that increments whenever collapsed state changes.
  // Used by external components to trigger reactivity when checking isItemCollapsed.
  const collapsedStateVersion = shallowRef(0);

  // O(1) lookup sets - IDs normalized to strings since external sources may mix types
  const initialExpandedSet = computed(
    () => new Set((initialExpandedItemIds?.value ?? []).map((id) => String(id)))
  );
  const initialCollapsedSet = computed(
    () =>
      new Set((initialCollapsedItemIds?.value ?? []).map((id) => String(id)))
  );

  // when using lazy mode, start with all collapsed (except items in initialExpandedItemIds)
  // Also explicitly collapse items in initialCollapsedItemIds (for items like initiatives
  // that wouldn't be auto-collapsed because they have childrenLoaded: true)
  watch(
    [lazy, denormalizedGanttItems],
    () => {
      if (!lazy.value) return;
      const items = denormalizedGanttItems.value;
      let stateChanged = false;

      items.forEach((item) => {
        const itemIdStr = String(item.id);

        // First, check if item should be explicitly collapsed (from initialCollapsedItemIds)
        // This handles items like initiatives that have childrenLoaded: true and wouldn't
        // be auto-collapsed by the lazy mode logic below
        if (
          initialCollapsedSet.value.has(itemIdStr) &&
          !collapsedItemTreesMap.value.has(item.id)
        ) {
          collapsedItemTreesMap.value.set(item.id, true);
          stateChanged = true;
          return; // Skip further processing for this item
        }

        // Skip if item is in the initial expanded list
        if (initialExpandedSet.value.has(itemIdStr)) {
          return;
        }

        // Auto-collapse items that don't have children loaded yet
        // !collapsedItemTreesMap.value.has(item.id) is to prevent unnecessary reactivity
        if (
          (item.type === 'wrapper' || item.type === 'group') &&
          !item.childrenLoaded &&
          !collapsedItemTreesMap.value.has(item.id)
        ) {
          collapsedItemTreesMap.value.set(item.id, true);
          stateChanged = true;
        }
      });
      // Increment version to trigger reactivity in external components
      if (stateChanged) {
        collapsedStateVersion.value++;
      }
    },
    { immediate: true }
  );

  function toggleItemCollapse(id: IGanttItem['id']) {
    const isCollapsed = !!collapsedItemTreesMap.value.get(id);
    if (isCollapsed) {
      collapsedItemTreesMap.value.set(id, false);
    } else {
      collapsedItemTreesMap.value.set(id, true);
    }
    // Increment version to trigger reactivity in external components
    collapsedStateVersion.value++;
    refreshExtendedGanttItemWithRectCache();
  }

  /**
   * Removes collapsed items from denormalizedGanttItems while also adjusting the _internalOrder and collapsedTreeHeight
   * of the remaining items to account for the removed items to help with rendering performance. Keep in mind,
   * this doesn't touch the nested objects (children, dependencies, dependents, parent, etc) of the items, so they still point to the
   * original items in denormalizedGanttItems. DO NOT use these nested objects for anything related to rendering
   * (e.g. the _internalOrder property).
   *
   * The returned array is sorted by _internalOrder.
   */
  const ganttItemsWithoutCollapsed = computed<IExtendedGanttItemWithCollapse[]>(
    () => {
      profile('ganttItemsWithoutCollapsed');
      const ret: IExtendedGanttItemWithCollapse[] = [];
      let idx = 0;
      let linearIdx = 0;
      while (idx < denormalizedGanttItems.value.length) {
        const currentItem = denormalizedGanttItems.value[idx];
        const isCollapsed = !!collapsedItemTreesMap.value.get(currentItem.id);

        const item: IExtendedGanttItemWithCollapse = {
          ...currentItem,
          _internalOrder: linearIdx,
          isCollapsed,
          collapsedTreeHeight: 0,
        };

        ret.push(item);
        if (isCollapsed) {
          idx += item.treeHeight;
        } else {
          idx++;
        }
        ++linearIdx;
      }

      const map = new Map<
        IExtendedGanttItemWithCollapse['id'],
        IExtendedGanttItemWithCollapse
      >();
      ret.forEach((item) => {
        map.set(item.id, item);
      });

      // DFS traversal with global visited set to prevent infinite recursion from circular refs
      const visitedItemIds = new Set<string>();
      const dfs = (item: IExtendedGanttItemWithCollapse) => {
        if (visitedItemIds.has(item.id)) return; // Already processed (circular ref or shared)
        visitedItemIds.add(item.id);

        item.collapsedTreeHeight++;
        if (collapsedItemTreesMap.value.get(item.id)) return;
        item.children.forEach(({ id: childId }) => {
          const child = map.get(childId);
          if (!child)
            throw new Error(
              `Child ${childId} not found in map. This is a bug in the component.`
            );
          dfs(child);
          item.collapsedTreeHeight += child.collapsedTreeHeight;
        });
      };
      ret.forEach((item) => {
        if (!item.parent) {
          dfs(item);
        }
      });

      profileEnd('ganttItemsWithoutCollapsed');
      return ret;
    }
  );

  const ganttItemsWithoutCollapsedMap = computed(() => {
    profile('ganttItemsWithoutCollapsedMap');
    const map = new Map<string, IExtendedGanttItemWithCollapse>();
    ganttItemsWithoutCollapsed.value.forEach((item) => {
      map.set(item.id, item);
    });
    profileEnd('ganttItemsWithoutCollapsedMap');
    return map;
  });

  /**
   * Removes collapsed items from ganttRelationships while also adjusting the minOrder and maxOrder of the remaining
   * ones to account for the removed items to help with rendering performance. Same disclaimer as
   * ganttItemsWithoutCollapsed applies here. DO NOT use the nested objects for anything related to rendering.
   */
  const ganttRelationshipsWithoutCollapsed = computed(() => {
    profile('ganttRelationshipsWithoutCollapsed');
    const ret: IGanttItemRelation[] = [];
    ganttRelationships.value.forEach((dep) => {
      const itemCurrent = ganttItemsWithoutCollapsedMap.value.get(
        dep.itemCurrent.id
      );
      const itemOther = ganttItemsWithoutCollapsedMap.value.get(
        dep.itemOther.id
      );
      if (itemCurrent && itemOther) {
        ret.push({
          ...dep,
          minOrder: Math.min(
            itemCurrent._internalOrder,
            itemOther._internalOrder
          ),
          maxOrder: Math.max(
            itemCurrent._internalOrder,
            itemOther._internalOrder
          ),
        });
      }
    });

    profileEnd('ganttRelationshipsWithoutCollapsed');
    return ret;
  });

  // ----------------- layer 2 <-> layer 3 bridge: rendering utilities ----------------- //

  // active items - these should be rendered unconditionally unless they are inside a collapsed tree
  // normally, there would only be one active item at a time, but the need for multiple active items might arise in the future
  const activeItemsMap = new Map<IGanttItem['id'], void>();
  const hasActiveItems = shallowRef(false);

  function markItemActive(id: IGanttItem['id']) {
    activeItemsMap.set(id);
    hasActiveItems.value = true;
  }

  function markItemInactive(id: IGanttItem['id']) {
    activeItemsMap.delete(id);
    hasActiveItems.value = activeItemsMap.size > 0;
  }

  // Clear Maps on unmount to prevent memory leaks from retained item ID references
  onUnmounted(() => {
    activeItemsMap.clear();
    collapsedItemTreesMap.value.clear();
  });

  /**
   * IExtendedGanttItemWithCollapse -> IExtendedGanttItemWithRect
   * Attaches rendering information to the item. Expensive to compute, so we maintain a cache below.
   * Not to be used directly, use `toExtendedGanttItemWithRectCached` instead.
   */
  const toExtendedGanttItemWithRect = (
    item: IExtendedGanttItemWithCollapse
  ): IExtendedGanttItemWithRect => {
    const rectStart = getDateRect(item._internalStartDate);
    const rectEnd = getDateRect(item._internalEndDate);

    const left = rectStart.left;
    const width = Math.max(rectEnd.left + rectEnd.width - rectStart.left, 4); // min width of 4px
    // Note: combinedHeaderHeight is NOT included here because items are rendered
    // inside a content container that's a sibling of the sticky header.
    // The header offset is handled at the layout level, not in item positions.
    const top =
      item._internalOrder * barHeight.value +
      barsContainerYPadding.value * barHeight.value;
    const height = barHeight.value;

    const rectTreeStart = getDateRect(item.minTreeDate);
    const rectTreeEnd = getDateRect(item.maxTreeDate);

    // Container should encompass BOTH the item's own dates AND the children's tree dates
    // This ensures the candy stripe shows when children extend beyond the parent in either direction
    const containerLeft = Math.min(rectTreeStart.left, left);
    const containerTop = top;
    const containerRight = Math.max(rectTreeEnd.right, rectStart.left + width);
    const containerWidth = containerRight - containerLeft;

    const containerHeight = barHeight.value * item.collapsedTreeHeight;

    const shadowRects: IExtendedGanttItemWithRect['rect']['shadows'] = {};
    item.shadows?.forEach((shadow) => {
      const shadowRectStart = getDateRect(shadow.startDate);
      const shadowRectEnd = getDateRect(shadow.endDate);

      const shadowLeft = shadowRectStart.left;
      const shadowWidth = Math.max(
        shadowRectEnd.left + shadowRectEnd.width - shadowRectStart.left,
        4 // min width of 4px
      );

      shadowRects[shadow.id] = {
        top,
        height,
        left: shadowLeft,
        width: shadowWidth,
      };
    });

    return {
      ...item,
      rect: {
        left,
        top,
        width,
        height,

        containerLeft,
        containerTop,
        containerWidth,
        containerHeight,

        shadows: shadowRects,
      },
    };
  };

  // caching layer for toExtendedGanttItemWithRect
  const extendedItemsWithRectCache = new Map<
    string,
    IExtendedGanttItemWithRect
  >();
  watch([denormalizedGanttItems, zoomLevelConfig, barHeight], () => {
    extendedItemsWithRectCache.clear();
  });
  // Clear cache on unmount to prevent memory leaks from retained IGanttItem references
  onUnmounted(() => {
    extendedItemsWithRectCache.clear();
  });
  const toExtendedGanttItemWithRectCached = (
    item: IExtendedGanttItemWithCollapse
  ): IExtendedGanttItemWithRect => {
    const cached = extendedItemsWithRectCache.get(item.id);
    if (cached) return cached;
    const ret = toExtendedGanttItemWithRect(item);
    extendedItemsWithRectCache.set(item.id, ret);

    const MAX_ITEMS_IN_CACHE = 500; // memory cap
    const ITEMS_TO_DELETE = 100; // create space for these many items if the cache is full
    if (extendedItemsWithRectCache.size > MAX_ITEMS_IN_CACHE) {
      // Use iterator-based deletion to avoid creating temporary array
      let deleteCount = 0;
      for (const key of extendedItemsWithRectCache.keys()) {
        if (deleteCount >= ITEMS_TO_DELETE) break;
        extendedItemsWithRectCache.delete(key);
        deleteCount++;
      }
    }

    return ret;
  };
  const refreshExtendedGanttItemWithRectCache = () => {
    extendedItemsWithRectCache.forEach((_, id) => {
      const item = ganttItemsWithoutCollapsedMap.value.get(id);
      if (!item) extendedItemsWithRectCache.delete(id);
      else
        extendedItemsWithRectCache.set(
          item.id,
          toExtendedGanttItemWithRect(item)
        );
    });
  };

  // ----------------- layer 3: externally consumable data for rendering ----------------- //
  // avoid layer 1 access from this point on to avoid nasty bugs. If unavoidable, comment instances of layer 1 access.

  /**
   * List of all gantt items that are present within the current virtualized (as opposed to rendered) view.
   * This accounts for collapsed items (i.e. if a parent is collapsed, its children are not included in this list).
   * Useful for implementing shift+click to select multiple items without incurring the cost of computing the rects.
   */
  const allVirtualizedGanttItems = computed<IGanttItem[]>(() => {
    return ganttItemsWithoutCollapsed.value;
  });

  const ganttItemsToRenderYAxis = computed<IExtendedGanttItemWithRect[]>(() => {
    // get items that lie in the currently rendered y-axis (itemsInCurrentVerticalView)
    const absoluteTolerance = Math.ceil(
      TOLERANCE_FACTOR_Y * (visibleContainerHeight.value / barHeight.value)
    );
    const heightToExhaust =
      visibleContainerHeight.value +
      visibleContainerHeight.value * TOLERANCE_FACTOR_Y * 2;
    const itemsToRenderCount = Math.ceil(heightToExhaust / barHeight.value);

    const itemStartIdx = Math.max(
      Math.floor(virtualScrollYOffset.value / barHeight.value) -
        absoluteTolerance +
        barsContainerYPadding.value,
      0
    );
    const itemsInCurrentVerticalView = ganttItemsWithoutCollapsed.value.slice(
      itemStartIdx,
      itemStartIdx + itemsToRenderCount
    );

    const existingItemIds = new Set(
      itemsInCurrentVerticalView.map((item) => item.id)
    );

    // add active items
    activeItemsMap.forEach((_, id) => {
      const item = ganttItemsWithoutCollapsedMap.value.get(id);
      if (item && !existingItemIds.has(item.id)) {
        itemsInCurrentVerticalView.push(item);
        existingItemIds.add(item.id);
      }
    });

    // add all parents so hierarchy containers are rendered
    itemsInCurrentVerticalView.forEach((item) => {
      let parentId = item.parentId;
      while (parentId) {
        const parent = ganttItemsWithoutCollapsedMap.value.get(parentId);
        if (!parent) {
          break;
        }
        if (!existingItemIds.has(parentId)) {
          itemsInCurrentVerticalView.push(parent);
          existingItemIds.add(parentId);
        }
        parentId = parent.parentId;
      }
    });

    // sort by _internalOrder
    itemsInCurrentVerticalView.sort(
      (a, b) => a._internalOrder - b._internalOrder
    );

    return itemsInCurrentVerticalView.map(toExtendedGanttItemWithRectCached);
  });
  const ganttItemsToRender = computed<IExtendedGanttItemWithRect[]>(() => {
    profile('ganttItemsToRender');
    // get items that lie in the currently rendered y-axis (itemsInCurrentVerticalView)
    const absoluteTolerance = Math.ceil(
      TOLERANCE_FACTOR_Y * (visibleContainerHeight.value / barHeight.value)
    );
    const heightToExhaust =
      visibleContainerHeight.value +
      visibleContainerHeight.value * TOLERANCE_FACTOR_Y * 2;
    const itemsToRenderCount = Math.ceil(heightToExhaust / barHeight.value);

    const itemStartIdx = Math.max(
      Math.floor(virtualScrollYOffset.value / barHeight.value) -
        absoluteTolerance +
        barsContainerYPadding.value,
      0
    );
    const itemsInCurrentVerticalView = ganttItemsWithoutCollapsed.value.slice(
      itemStartIdx,
      itemStartIdx + itemsToRenderCount
    );

    // narrow further down to items that lie in the currently rendered x-axis (itemsInCurrentHorizontalView)
    const minDate = minRenderedDate.value.valueOf();
    const maxDate = maxRenderedDate.value.valueOf();
    const itemsInCurrentHorizontalView: IExtendedGanttItemWithCollapse[] =
      itemsInCurrentVerticalView.filter(
        (item) =>
          item._minRenderDate.valueOf() <= maxDate &&
          item._maxRenderDate.valueOf() >= minDate
      );
    const existingItemIds = new Set(
      itemsInCurrentHorizontalView.map((item) => item.id)
    );

    // add all parents so hierarchy containers are rendered
    itemsInCurrentHorizontalView.forEach((item) => {
      let parentId = item.parentId;
      const visitedParentIds = new Set<string>();
      while (parentId) {
        if (visitedParentIds.has(parentId)) {
          break; // Prevent infinite loop from circular parent references
        }
        visitedParentIds.add(parentId);
        const parent = ganttItemsWithoutCollapsedMap.value.get(parentId);
        if (!parent) {
          break;
        }
        if (!existingItemIds.has(parentId)) {
          itemsInCurrentHorizontalView.push(parent);
          existingItemIds.add(parentId);
        }
        parentId = parent.parentId;
      }
    });

    // add active items
    activeItemsMap.forEach((_, id) => {
      const item = ganttItemsWithoutCollapsedMap.value.get(id);
      if (item && !existingItemIds.has(item.id)) {
        itemsInCurrentHorizontalView.push(item);
      }
    });
    // and sort again
    itemsInCurrentHorizontalView.sort(
      (a, b) => a._internalOrder - b._internalOrder
    );

    const itemsToRender: IExtendedGanttItemWithRect[] =
      itemsInCurrentHorizontalView.map(toExtendedGanttItemWithRectCached);

    profileEnd('ganttItemsToRender');
    return itemsToRender;
  });
  const ganttRelationshipsToRender = computed<IGanttItemRelationWithRect[]>(
    () => {
      profile('ganttRelationshipsToRender');

      // get item count to render based on currently displayed y-axis, accounting for tolerance
      const absoluteTolerance = Math.ceil(
        TOLERANCE_FACTOR_Y * (visibleContainerHeight.value / barHeight.value)
      );
      const heightToExhaust =
        visibleContainerHeight.value +
        visibleContainerHeight.value * TOLERANCE_FACTOR_Y * 2;
      const itemsToRenderCount = Math.ceil(heightToExhaust / barHeight.value);

      // get start index/_internalOrder
      const itemStartOrder = Math.max(
        Math.floor(virtualScrollYOffset.value / barHeight.value) -
          absoluteTolerance,
        0
      );

      // filtering criteria
      const minDate = minRenderedDate.value.valueOf();
      const maxDate = maxRenderedDate.value.valueOf();
      const minOrder = itemStartOrder;
      const maxOrder = minOrder + itemsToRenderCount;

      // NOTE: the assumption (more like hope) is that there won't be too many total relationships and
      // so iterating over all of them is not too expensive. If this stops being the case, we will
      // need to figure out some other way (quadtree) to narrow down the number of items we look through.
      const depsToRender: IGanttItemRelationWithRect[] =
        ganttRelationshipsWithoutCollapsed.value
          .filter((dep) => {
            return (
              dep.minDate.valueOf() <= maxDate &&
              dep.maxDate.valueOf() >= minDate &&
              dep.minOrder <= maxOrder &&
              dep.maxOrder >= minOrder
            );
          })
          .map((dep) => {
            // NOTE: ganttRelationshipsWithoutCollapsed guarantees that the dependency and dependent exist in the map

            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const itemCurrent = ganttItemsWithoutCollapsedMap.value.get(
              dep.itemCurrent.id
            )!;
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const itemOther = ganttItemsWithoutCollapsedMap.value.get(
              dep.itemOther.id
            )!;
            // Avoid double-spreading by explicitly constructing the object
            // (dep was already spread in ganttRelationshipsWithoutCollapsed)
            const depWithRect: IGanttItemRelationWithRect = {
              id: dep.id,
              relationDef: dep.relationDef,
              minDate: dep.minDate,
              maxDate: dep.maxDate,
              minOrder: dep.minOrder,
              maxOrder: dep.maxOrder,
              itemCurrent: toExtendedGanttItemWithRectCached(itemCurrent),
              itemOther: toExtendedGanttItemWithRectCached(itemOther),
            };
            return depWithRect;
          });
      profileEnd('ganttRelationshipsToRender');
      return depsToRender;
    }
  );
  const isItemCollapsed = (id: IGanttItem['id']) => {
    return collapsedItemTreesMap.value.get(id) === true;
  };

  // ----------------- dimensions ----------------- //
  const virtualScrollWidth = computed(() => {
    return (
      maxDate.value.diff(minDate.value, 'day') *
      zoomLevelConfig.value.dayBlockWidth
    );
  });
  const virtualScrollHeight = computed(() => {
    return (
      ganttItemsWithoutCollapsed.value.length * barHeight.value + // items
      combinedHeaderHeight.value + // headers
      barsContainerYPadding.value * barHeight.value * 2
    ); // padding
  });

  return {
    // header blocks
    blocksToRender,
    subBlocksToRender,
    contextualBlocksToRender,
    customBlocksToRender,

    // positioning
    approxCurrentlyCenteredDate,
    approxCurrentlyCenteredRowIndex,
    stackedHeadersCount,
    combinedHeaderHeight,
    minRenderedDate,
    maxRenderedDate,
    getDateRect,
    getDateByLeftOffset,

    // programmatic scrolling
    scrollToDate,
    scrollToToday,
    scrollToRowIndex,
    scrollToCenterPoint,
    onProgrammaticScrollOffsetChangeReactor,

    // zooming
    canZoomIn,
    canZoomOut,
    incrementZoomLevel,
    decrementZoomLevel,
    setZoomLevel,
    setZoomLevelIdx,
    findClosestZoomLevelIdx,
    currentZoomLevelIdx,
    zoomLevelCount,
    zoomLevelConfig,
    previousZoomLevelConfig,
    nextZoomLevelConfig,

    // gantt items
    allVirtualizedGanttItems,
    ganttItemsToRenderYAxis,
    ganttItemsToRender,
    ganttRelationshipsToRender,
    markItemActive,
    markItemInactive,
    hasActiveItems,
    toggleItemCollapse,
    isItemCollapsed,
    collapsedStateVersion,

    // dimensions
    virtualScrollWidth,
    virtualScrollHeight,
  };
};

/**
 * Converts a mouse event to a position relative to the scroller element.
 */
export const mouseEventToScrollerPosition = ({
  event,
  scrollerEl,
  virtualScrollXOffset,
  virtualScrollYOffset,
}: {
  event: MouseEvent;
  scrollerEl: HTMLElement;
  virtualScrollXOffset: number;
  virtualScrollYOffset: number;
}) => {
  const scrollerRect = scrollerEl.getBoundingClientRect();
  const absLeft = virtualScrollXOffset + (event.x - scrollerRect.left);
  const absTop = virtualScrollYOffset + (event.y - scrollerRect.top);
  return { x: absLeft, y: absTop };
};

// ------------------------------ relationship painting hook and utils ----------------------------- //

const DEP_LINE_OFFSET_X = 10;
const DEP_LINE_OFFSET_Y = 5;

const getCurveType = (
  fromItemRect: { x: number; y: number; width: number; height: number },
  toItemRect: { x: number; y: number; width: number; height: number },
  connectionType: ConnectionType
) => {
  let x1 = fromItemRect.x;
  if (connectionType[0] === 'end') {
    x1 += fromItemRect.width;
  }
  const y1 = fromItemRect.y + fromItemRect.height / 2;

  let x2 = toItemRect.x;
  if (connectionType[1] === 'end') {
    x2 += toItemRect.width;
  }
  const y2 = toItemRect.y + toItemRect.height / 2;

  const connectionTypeComputed = connectionType.join('->') as
    | 'start->start'
    | 'start->end'
    | 'end->start'
    | 'end->end';

  const isX1LessThanX2 = x1 < x2 && Math.abs(x1 - x2) > DEP_LINE_OFFSET_X * 2;
  const isY1LessThanY2 = y1 < y2 && Math.abs(y1 - y2);
  const isX1GreaterThanX2 = !isX1LessThanX2;
  const isY1GreaterThanY2 = !isY1LessThanY2;

  // if conditions are accounting for all 16 cases in the cartesian product of
  // [start, end] x [start, end] x [x1 < x2, x1 > x2] x [y1 < y2, y1 > y2]

  // specific return value for each condition was determined by comparing the behavior against Wrike's implementation.
  // there might be a much more abstract way to do this, but this works for now.

  // start to start (tested)
  if (connectionTypeComputed === 'start->start') {
    if (isX1LessThanX2 && isY1LessThanY2) {
      return curveStepBefore; // tested
    }
    if (isX1LessThanX2 && isY1GreaterThanY2) {
      return curveStepBefore; // tested
    }
    if (isX1GreaterThanX2 && isY1LessThanY2) {
      return curveStepAfter; // tested
    }
    if (isX1GreaterThanX2 && isY1GreaterThanY2) {
      return curveStepAfter; // tested
    }
  }
  // start to end (tested)
  if (connectionTypeComputed === 'start->end') {
    if (isX1LessThanX2 && isY1LessThanY2) {
      return curveStepBefore; // tested
    }
    if (isX1LessThanX2 && isY1GreaterThanY2) {
      return curveStepBefore; // tested
    }
    if (isX1GreaterThanX2 && isY1LessThanY2) {
      return curveStepBefore; // tested
    }
    if (isX1GreaterThanX2 && isY1GreaterThanY2) {
      return curveStepAfter; // tested
    }
  }
  // end to start (tested)
  if (connectionTypeComputed === 'end->start') {
    if (isX1LessThanX2 && isY1LessThanY2) {
      return curveStepBefore; // tested
    }
    if (isX1LessThanX2 && isY1GreaterThanY2) {
      return curveStepBefore; // tested
    }
    if (isX1GreaterThanX2 && isY1LessThanY2) {
      return curveStepBefore; // tested
    }
    if (isX1GreaterThanX2 && isY1GreaterThanY2) {
      return curveStepBefore; // tested
    }
  }
  // end to end (tested)
  if (connectionTypeComputed === 'end->end') {
    if (isX1LessThanX2 && isY1LessThanY2) {
      return curveStepAfter; // tested
    }
    if (isX1LessThanX2 && isY1GreaterThanY2) {
      return curveStepAfter; // tested
    }
    if (isX1GreaterThanX2 && isY1LessThanY2) {
      return curveStepBefore; // tested
    }
    if (isX1GreaterThanX2 && isY1GreaterThanY2) {
      return curveStepBefore; // tested
    }
  }
  return curveStepBefore; // shouldn't reach here
};
const getLeftByConnectionType = (
  rect: { x: number; width: number },
  connectionType: 'start' | 'end'
) => {
  if (connectionType === 'start') {
    return rect.x;
  }
  return rect.x + rect.width;
};

type Coordinate = [number, number];
const rotateCoordAroundPoint = (
  [coordX, coordY]: Coordinate,
  [pointX, pointY]: Coordinate,
  angleDeg: number
): Coordinate => {
  const x = coordX - pointX;
  const y = coordY - pointY;
  const rad = angleDeg * (Math.PI / 180);
  const rotatedX = x * Math.cos(rad) - y * Math.sin(rad);
  const rotatedY = x * Math.sin(rad) + y * Math.cos(rad);
  return [rotatedX + pointX, rotatedY + pointY];
};

export function useDependencyConnector({
  fromItemRect,
  toItemRect,
  connectionType,
}: {
  fromItemRect: Ref<{ x: number; y: number; width: number; height: number }>;
  toItemRect: Ref<{ x: number; y: number; width: number; height: number }>;
  connectionType: Ref<ConnectionType> | ConnectionType;
}) {
  const dependencyLinePath = computed(() => {
    const connType = unref(connectionType);
    const mainCurveGen = line().curve(
      getCurveType(fromItemRect.value, toItemRect.value, connType)
    );
    const startCoord = {
      x: getLeftByConnectionType(fromItemRect.value, connType[0]),
      y: fromItemRect.value.y + fromItemRect.value.height / 2,
    };
    const endCoord = {
      x: getLeftByConnectionType(toItemRect.value, connType[1]),
      y: toItemRect.value.y + toItemRect.value.height / 2,
    };

    const hasDirectPath = (() => {
      const connectionTypeComputed = connType.join('->') as
        | 'start->start'
        | 'start->end'
        | 'end->start'
        | 'end->end';
      if (
        connectionTypeComputed === 'start->start' ||
        connectionTypeComputed === 'end->end'
      ) {
        return true;
      }
      const hasEnoughSpaceForOffsetBends =
        Math.abs(endCoord.x - startCoord.x) > 2 * DEP_LINE_OFFSET_X;
      if (connectionTypeComputed === 'start->end') {
        return startCoord.x > endCoord.x && hasEnoughSpaceForOffsetBends;
      }
      if (connectionTypeComputed === 'end->start') {
        return startCoord.x < endCoord.x && hasEnoughSpaceForOffsetBends;
      }
      return false;
    })();

    const lineCoord2 = {
      x:
        connType[0] === 'start'
          ? startCoord.x - DEP_LINE_OFFSET_X
          : startCoord.x + DEP_LINE_OFFSET_X,
      y: startCoord.y,
    };

    const lineCoord3 = {
      x:
        connType[1] === 'start'
          ? endCoord.x - DEP_LINE_OFFSET_X
          : endCoord.x + DEP_LINE_OFFSET_X,
      y: hasDirectPath
        ? endCoord.y
        : startCoord.y < endCoord.y
        ? toItemRect.value.y - DEP_LINE_OFFSET_Y
        : toItemRect.value.y + toItemRect.value.height + DEP_LINE_OFFSET_Y,
    };

    const pathD =
      mainCurveGen(
        [startCoord, lineCoord2, lineCoord3, endCoord].map((d) => [d.x, d.y])
      ) || '';
    const pathDStraight =
      line()([startCoord, endCoord].map((d) => [d.x, d.y])) || '';

    const arrowPathGen = (angleDeg = 0) => {
      const ARROW_SIZE = 5;
      const HALF_ARROW_SIZE = ARROW_SIZE / 2;

      const { x, y } = endCoord;
      const [sx, sy] =
        connType[1] === 'start'
          ? [x - ARROW_SIZE, y - HALF_ARROW_SIZE]
          : [x + ARROW_SIZE, y - HALF_ARROW_SIZE];

      const point: Coordinate = [x, y];
      const start = rotateCoordAroundPoint([sx, sy], point, angleDeg);
      const middle = rotateCoordAroundPoint([x, y], point, angleDeg);
      const end = rotateCoordAroundPoint(
        [sx, sy + ARROW_SIZE],
        point,
        angleDeg
      );
      return `M ${start} L ${middle} L ${end}`;
    };

    const pathDWithArrow = `${pathD} ${arrowPathGen()}`;

    const angleStraight =
      (Math.atan2(endCoord.y - startCoord.y, endCoord.x - startCoord.x) * 180) /
      Math.PI;
    const pathDWithArrowStraight = `${pathDStraight} ${arrowPathGen(
      angleStraight
    )}`;

    return {
      pathD,
      pathDStraight,
      pathDWithArrow,
      pathDWithArrowStraight,
    };
  });

  return {
    dependencyLinePath,
  };
}

const getPointDistance = (
  p1: { x: number; y: number },
  p2: { x: number; y: number }
) => {
  const a = p1.x - p2.x;
  const b = p1.y - p2.y;
  return Math.sqrt(a * a + b * b);
};

export function getCoordsOnPathNearClickPosition({
  scrollerEl,
  svgPath,
  mouseEvent,
  virtualScrollXOffset,
  virtualScrollYOffset,
  proximityThreshold = 20,
}: {
  scrollerEl: HTMLElement;
  svgPath: SVGPathElement;
  mouseEvent: MouseEvent;
  virtualScrollXOffset: number;
  virtualScrollYOffset: number;
  proximityThreshold?: number;
}) {
  const clickPosition = mouseEventToScrollerPosition({
    event: mouseEvent,
    scrollerEl,
    virtualScrollXOffset,
    virtualScrollYOffset,
  });
  const pathLength = svgPath.getTotalLength();

  // linear search to find the closest point on the path to the click position based on proximityThreshold
  let closestPoint: DOMPoint | null = null;
  let closestPointDistance = Infinity;
  const trimOffset = Math.min(Math.floor(pathLength * 0.15), 30); // trim the ends of the path so the position isn't near the edges
  for (let i = trimOffset; i < pathLength - trimOffset; i++) {
    const point = svgPath.getPointAtLength(i);
    const pointDistance = getPointDistance(point, clickPosition);
    if (pointDistance < closestPointDistance) {
      closestPoint = point;
      closestPointDistance = pointDistance;
    }
    if (pointDistance < proximityThreshold) {
      break;
    }
  }

  return closestPoint
    ? { x: closestPoint.x, y: closestPoint.y }
    : clickPosition;
}

export function adjustXAndWidthForMilestone(
  sideLength: number,
  x: number,
  type: 'start' | 'end'
) {
  const width = sideLength * Math.sqrt(2);
  const offset = width / 2;
  x = x - offset;
  if (type === 'start') {
    x += 1; // +1 accounts for border-radius
  } else if (type === 'end') {
    x -= 2; // -2 accounts for border-radius (no arrow so requires 2px)
  }
  return { x, width };
}

// ------------------------------ component specific shared stuff ----------------------------- //

export const GANTT_BAR_RENDERING_CONFIG = Object.freeze({
  ITEM_HEIGHT_PERCENT: 0.45,
  ITEM_HEIGHT_PERCENT_MILESTONE: 0.55,
  ITEM_TOP_OFFSET_PERCENT: 0.2,
  CONTAINER_BOTTOM_OFFSET_PERCENT: 0.1,
  getItemTopWithMargin: (top: number, barHeight: number) => {
    return top + barHeight * GANTT_BAR_RENDERING_CONFIG.ITEM_TOP_OFFSET_PERCENT;
  },
  getItemHeight: (height: number, isMilestone = false) => {
    if (isMilestone) {
      return height * GANTT_BAR_RENDERING_CONFIG.ITEM_HEIGHT_PERCENT_MILESTONE;
    }
    return height * GANTT_BAR_RENDERING_CONFIG.ITEM_HEIGHT_PERCENT;
  },
  getItemTreeContainerHeight: (height: number, barHeight: number) => {
    const {
      ITEM_HEIGHT_PERCENT,
      ITEM_TOP_OFFSET_PERCENT,
      CONTAINER_BOTTOM_OFFSET_PERCENT,
    } = GANTT_BAR_RENDERING_CONFIG;
    const remainingSpaceAtItemBottom =
      barHeight - barHeight * (ITEM_HEIGHT_PERCENT + ITEM_TOP_OFFSET_PERCENT);
    const bottomOffset =
      remainingSpaceAtItemBottom - barHeight * CONTAINER_BOTTOM_OFFSET_PERCENT; // end the container slightly below the bottom of the bar
    const ret = height - barHeight * ITEM_TOP_OFFSET_PERCENT - bottomOffset;
    return ret;
  },
});

export interface IHeaderHeightsConfig {
  /** height of the main header */
  main: number;
  /** height of the sub header(s) */
  sub: number;
}

export interface IColorConfig {
  /**
   * Used for miscellaneous things where needed.
   */
  primary: string;
  /**
   * Used for miscellaneous things where needed.
   */
  success: string;
  /**
   * Used for miscellaneous things where needed.
   */
  error: string;

  /**
   * color of the background of the non-rendered part of the scrollable area, might show up when scrolling extremely fast.
   * set it to the same color as the background of the container if you don't want to see it (which you probably don't).
   */
  nonRenderedAreaBackground: string;
  /**
   * color of the background of the rendered part of the scrollable area
   */
  renderedAreaBackground: string;
  /**
   * color of the background of the rendered part of the table
   */
  tableRenderedAreaBackground: string;

  /**
   * this will be used as the background color for the controls (zoom buttons, etc)
   */
  controlsBackground: string;
  controlsBorder: string;
  /**
   * this will be used as the background color for the header blocks
   */
  headerBlocksBackground: string;
  /**
   * this will be used as the background color for the header block dividers
   */
  headerBlocksDividerBackground: string;

  /**
   * this will be used as the background color for the weekend markers (if enabled)
   */
  weekendMarkersBackground: string;

  /**
   * this will be used as the background color for the current time marker (if enabled)
   */
  todayMarkerBackground: string;

  /**
   * this will be used as the stroke color for the dependency lines.
   * also accepts the following special values:
   * - 'relationColor': will use the color of the relation
   * - 'itemCurrent': will use the color of the current item
   * - 'itemOther': will use the color of the other item
   */
  relationLineStroke:
    | 'relationColor'
    | 'itemCurrent'
    | 'itemOther'
    | string
    | ((dep: IGanttItemRelationWithRect) => string);

  /**
   * this will be used as the hover background for the items/horizontal blocks with an opacity of 0.3.
   */
  itemHoverBackground: string;

  /**
   * this will be used as the stroke color for the relation line during drag (before connecting to a target).
   */
  relationDragStroke: string;
}

export interface IColumnConfig {
  id: string;
  isIdentifier?: boolean;

  label: string;
  value?: (item: IExtendedGanttItem) => Primitive;

  minWidth: number;
  width: number;
  maxWidth: number;
}

export interface ISelectionConfig {
  /** whether or not to allow item selection */
  enabled: boolean;
  /** maximum number of items that can be selected at once. Infinity for unlimited. */
  maxItems: number;
  /** which item types can be selected */
  selectableTypes:
    | {
        [key in IGanttItem['type']]?: boolean;
      }
    | 'all';
}

export interface IRelationConfig {
  relationDefs: IGanttRelationDef[];
  isRelationValid: (arg: {
    itemCurrent: IExtendedGanttItemWithRect;
    itemOther: IExtendedGanttItemWithRect;
    relationDef: IGanttRelationDef | null;
  }) => boolean;
  relationLineStrokeWidth: string;
}

export interface IGanttCustomizations {
  isTableVisible: boolean;
  sidebarWidth: number;
  tableColumnWidths: Record<IColumnConfig['id'], IColumnConfig['width']>;
  showBoundingBoxes: boolean;
  showInlineLabels: boolean;
}

export const DEFAULT_GANTT_CUSTOMIZATIONS = (): IGanttCustomizations => ({
  isTableVisible: true,
  sidebarWidth: 0,
  tableColumnWidths: {},
  showBoundingBoxes: true,
  showInlineLabels: true,
});

// ------------------------------ utils ----------------------------- //

export function useClickOutside(
  ref: Ref<HTMLElement | null>,
  handler: (e: MouseEvent) => void
) {
  const listener = (e: MouseEvent) => {
    const el = ref.value;
    if (!el || el.contains(e.target as HTMLElement)) {
      return;
    }
    handler(e);
  };
  const removeListener = () => {
    document.removeEventListener('mousedown', listener, { capture: true });
  };
  watch(
    ref,
    (newVal, oldVal) => {
      if (oldVal) {
        removeListener();
      }
      if (newVal) {
        document.addEventListener('mousedown', listener, { capture: true });
      }
    },
    { immediate: true }
  );
  onUnmounted(() => {
    removeListener();
  });
}

/**
 * Converts [left, top, width, height] to [left, top, width, height]px in an object (if needed).
 * Returns a new object with all other keys intact.
 */
export const rectToPx = (obj: { [key: string]: number | string }) => {
  const propsToConvert = ['left', 'top', 'width', 'height', 'minWidth'];
  const ret: { [key: string]: string | number } = {};
  Object.entries(obj).forEach(([key, val]) => {
    if (propsToConvert.includes(key) && typeof val === 'number') {
      ret[key] = toPx(val);
    } else {
      ret[key] = val;
    }
  });
  return ret;
};

/**
 * timeout promise
 */
export function timeout(ms = 0) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// rounding utilities

export function floorToMultiple(val: number, multiple: number) {
  return Math.floor(val / multiple) * multiple;
}

export function ceilToMultiple(val: number, multiple: number) {
  return Math.ceil(val / multiple) * multiple;
}

export type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;
export type Primitive = string | number | boolean;
